import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@flowcore-water/ui';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { libraryService } from '../../services/libraryService';
import { attestationService } from '../../services/attestationService';
import { storage, isDemoMode } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import '@googleworkspace/drive-picker-element';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { SKILL_TREES } from '../../data/skillTrees';
import { getManualSection, MANUAL_PAGE_PATH } from '../../data/trainingManual';
import type { LibraryItem, LibraryItemType } from '../../types';

type AttachmentSource = 'upload' | 'link' | 'drive';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_APP_ID = import.meta.env.VITE_GOOGLE_APP_ID || '';

export const LibraryItemEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTheme();
  const { user } = useAuth();
  const { refreshUsers, qualifications: qualificationsData } = useData(undefined);
  const navigate = useNavigate();

  const isNew = !id || id === 'new';

  // Opened from the training manual page: /library/new?manual=<sectionId>&week=<n>
  const [searchParams] = useSearchParams();
  const initialManual = isNew ? getManualSection(searchParams.get('manual')) : undefined;
  const initialWeek = parseInt(searchParams.get('week') || '', 10);

  const [manualSection, setManualSection] = useState<string | null>(initialManual?.id ?? null);
  const [manualWeek, setManualWeek] = useState<string>(
    initialManual && Number.isFinite(initialWeek) ? String(initialWeek) : ''
  );
  const manualInfo = getManualSection(manualSection);

  const [itemType, setItemType] = useState<LibraryItemType>(initialManual ? 'pdf' : 'article');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [url, setUrl] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [linkedQualIds, setLinkedQualIds] = useState<Set<string>>(new Set());
  const [skillTreeIds, setSkillTreeIds] = useState<Set<string>>(new Set());
  const [attachmentSource, setAttachmentSource] = useState<AttachmentSource>('link');
  const [driveUrl, setDriveUrl] = useState('');
  const pickerRef = useRef<any>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [existingItem, setExistingItem] = useState<LibraryItem | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = await libraryService.getById(id!);
        if (data) {
          setExistingItem(data);
          setItemType(data.type);
          setTitle(data.title);
          setDescription(data.description);
          setLinkedQualIds(new Set(data.linkedQualIds || (data as any).linkedItemIds || []));
          setSkillTreeIds(new Set(data.skillTreeIds || []));
          setManualSection(data.manualSection || null);
          setManualWeek(data.manualWeek != null ? String(data.manualWeek) : '');

          if (data.type === 'article') setBody(data.body);
          else if (data.type === 'pdf') setFileUrl(data.fileUrl);
          else if (data.type === 'weblink') {
            setUrl(data.url);
            setScreenshotUrl(data.screenshotUrl);
          }
        } else {
          setError('Item not found');
        }
      } catch (err) {
        console.error('Error loading item:', err);
        setError('Failed to load item');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    const weekNum = parseInt(manualWeek, 10);
    if (manualInfo && (!Number.isFinite(weekNum) || weekNum < 1)) {
      setError('Week number is required');
      return;
    }
    if (!changeSummary.trim()) {
      setError('Change summary is required');
      return;
    }
    if (!user) {
      setError('Not authenticated');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const itemData: any = {
        id: existingItem?.id,
        type: itemType,
        title: title.trim(),
        description: description.trim(),
        linkedQualIds: Array.from(linkedQualIds),
        skillTreeIds: Array.from(skillTreeIds),
        manualSection: manualInfo ? manualInfo.id : null,
        manualWeek: manualInfo ? weekNum : null,
      };

      if (itemType === 'article') itemData.body = body;
      else if (itemType === 'pdf') itemData.fileUrl = fileUrl.trim();
      else if (itemType === 'weblink') {
        itemData.url = url.trim();
        itemData.screenshotUrl = screenshotUrl.trim();
      }

      const saved = await libraryService.save(
        itemData,
        changeSummary.trim(),
        user.uid,
        user.displayName
      );

      // Create/update attestations on edit (version > 1)
      if (existingItem && saved.version > 1) {
        // Re-fetch users to include any added in other sessions
        const freshUsers = await refreshUsers();
        await attestationService.createForArticleUpdate(
          saved.id,
          saved.title,
          saved.version,
          Array.from(linkedQualIds),
          changeSummary.trim(),
          freshUsers
        );
      }

      navigate(manualInfo ? MANUAL_PAGE_PATH : `/library/${saved.slug}`);
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleQualLink = (qualId: string) => {
    const newSet = new Set(linkedQualIds);
    if (newSet.has(qualId)) newSet.delete(qualId);
    else newSet.add(qualId);
    setLinkedQualIds(newSet);
  };

  const toggleSkillTree = (treeId: string) => {
    const newSet = new Set(skillTreeIds);
    if (newSet.has(treeId)) newSet.delete(treeId);
    else newSet.add(treeId);
    setSkillTreeIds(newSet);
  };

  // Wire up Drive Picker events via ref
  useEffect(() => {
    const el = pickerRef.current;
    if (!el) return;
    const handlePicked = (e: any) => {
      const doc = e.detail?.docs?.[0];
      if (doc) {
        const id = doc.id;
        setFileUrl(`https://drive.google.com/file/d/${id}/preview`);
        setDriveUrl(doc.url || `https://drive.google.com/file/d/${id}/view`);
      }
    };
    el.addEventListener('picker-picked', handlePicked);
    return () => el.removeEventListener('picker-picked', handlePicked);
  }, [attachmentSource]);

  /** Convert a Google Drive sharing URL to an embeddable preview URL */
  const normalizeDriveUrl = (raw: string): string => {
    // Extract file ID from various Drive URL formats
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9_-]+)/,        // /file/d/ID
      /id=([a-zA-Z0-9_-]+)/,                 // ?id=ID
      /\/open\?id=([a-zA-Z0-9_-]+)/,         // /open?id=ID
    ];
    for (const p of patterns) {
      const m = raw.match(p);
      if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
    }
    return raw; // return as-is if we can't parse it
  };

  const handleDriveApply = () => {
    if (!driveUrl.trim()) return;
    setFileUrl(normalizeDriveUrl(driveUrl.trim()));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (isDemoMode || !storage) {
      // In demo mode, create an object URL as a placeholder
      const objectUrl = URL.createObjectURL(file);
      setFileUrl(objectUrl);
      setUploadFileName(file.name);
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadFileName(file.name);

      const storageRef = ref(storage, `library-attachments/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        },
        (err) => {
          console.error('Upload failed:', err);
          // Surface the Storage error code (e.g. storage/unauthorized) so failures can be diagnosed
          const code = (err as { code?: string }).code;
          setError(
            code === 'storage/unauthorized'
              ? `File upload failed: permission denied (${code}). Your account may not have upload access to Storage.`
              : `File upload failed${code ? ` (${code})` : ''}: ${err.message}`
          );
          setUploading(false);
        },
        async () => {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          setFileUrl(downloadUrl);
          setUploading(false);
        }
      );
    } catch (err) {
      console.error('Upload error:', err);
      setError(`File upload failed: ${err instanceof Error ? err.message : String(err)}`);
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="" style={{ color: t.textMuted }}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <Link to="/library" className="inline-flex items-center gap-2  hover:text-cyan-300 transition-colors" style={{ color: t.accent }}>
          ← Back to Library
        </Link>
        <AlertBanner variant="error" message="Not authorized" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={manualInfo ? MANUAL_PAGE_PATH : '/library'}
        className="inline-flex items-center gap-2  hover:text-cyan-300 transition-colors" style={{ color: t.accent }}
      >
        ← Back to {manualInfo ? 'Water Well Technician Training' : 'Library'}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor Pane */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border   p-6 space-y-4">
            {/* Type selector (only for new items) */}
            {isNew && (
              <div>
                <label className="block text-xs font-bold  mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                  Document Type
                </label>
                <div className="flex gap-2">
                  {(['article', 'pdf', 'weblink'] as LibraryItemType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setItemType(type)}
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                      style={itemType === type
                        ? { backgroundColor: t.accentDim, color: t.textPrimary }
                        : { color: t.textMuted }}
                    >
                      {type === 'article' ? 'Article' : type === 'pdf' ? 'Attachment' : 'Web Link'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {manualInfo && (
              <div>
                <label className="block text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                  {manualInfo.title} — Week
                </label>
                <input
                  type="number"
                  min={1}
                  placeholder="Week number"
                  value={manualWeek}
                  onChange={(e) => setManualWeek(e.target.value)}
                  className="w-32 rounded-lg border px-4 py-2 placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
                  style={{ color: t.textPrimary }}
                />
              </div>
            )}

            <input
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
            />

            <textarea
              placeholder="Description (shown in library list and as canonical summary)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none resize-none" style={{ color: t.textPrimary }}
            />

            <textarea
              placeholder="Change Summary"
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
              className="w-full rounded-lg border px-4 py-2 placeholder-slate-500 focus:outline-none h-20 resize-none"
              style={{
                color: t.textPrimary,
                borderColor: existingItem ? '#b45309' : t.border,
                borderWidth: existingItem ? 2 : 1,
              }}
            />

            {/* Type-specific fields */}
            {itemType === 'article' && (
              <>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode('edit')}
                    className="px-3 py-1 rounded font-medium transition-colors"
                    style={mode === 'edit'
                      ? { backgroundColor: t.accentDim, color: t.textPrimary }
                      : { color: t.textSecondary }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setMode('preview')}
                    className="px-3 py-1 rounded font-medium transition-colors"
                    style={mode === 'preview'
                      ? { backgroundColor: t.accentDim, color: t.textPrimary }
                      : { color: t.textSecondary }}
                  >
                    Preview
                  </button>
                </div>

                {mode === 'edit' ? (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Article content (Markdown supported)"
                    className="w-full rounded-lg border   px-4 py-3  placeholder-slate-500 focus:border-cyan-400 focus:outline-none font-mono text-sm h-96 resize-none" style={{ color: t.textPrimary }}
                  />
                ) : (
                  <div className="rounded-lg border   p-4 h-96 overflow-auto">
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeSlug]}
                        components={{
                          h1: (props: any) => <h2 className="text-2xl font-bold  mt-4 mb-2" style={{ color: t.textPrimary }} {...props} />,
                          h2: (props: any) => <h3 className="text-xl font-bold  mt-3 mb-2" style={{ color: t.textPrimary }} {...props} />,
                          p: (props: any) => <p className=" mb-2 text-sm" style={{ color: t.textSecondary }} {...props} />,
                          ul: (props: any) => <ul className="list-disc list-inside  mb-2 text-sm" style={{ color: t.textSecondary }} {...props} />,
                          code: ({ inline, ...props }: any) =>
                            inline ? (
                              <code className=" text-cyan-300 px-1 rounded font-mono text-xs" {...props} />
                            ) : (
                              <code className=" text-cyan-300 px-2 py-1 rounded font-mono text-xs block my-2" {...props} />
                            ),
                          a: (props: any) => <a className=" hover:text-cyan-300 underline" style={{ color: t.accent }} {...props} />,
                        }}
                      >
                        {body || ''}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </>
            )}

            {itemType === 'pdf' && (
              <div className="space-y-4">
                <label className="block text-xs font-bold  mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                  Attachment Source
                </label>

                {/* Source tabs */}
                <div className="flex gap-1">
                  {(['upload', 'link', 'drive'] as AttachmentSource[]).map((src) => (
                    <button
                      key={src}
                      onClick={() => setAttachmentSource(src)}
                      title={src === 'upload' ? 'Upload File' : src === 'link' ? 'Paste URL' : 'Google Drive'}
                      className="p-2 rounded-lg transition-colors"
                      style={attachmentSource === src
                        ? { backgroundColor: t.accentDim, color: t.textPrimary }
                        : { color: t.textMuted }}
                    >
                      {src === 'upload' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      )}
                      {src === 'link' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                      )}
                      {src === 'drive' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 6 6.55-11.5zm1.14 0l6.57 11.5H22L15.43 3.5zm7.72 12.5H2.29l3.43 6h11.43z"/></svg>
                      )}
                    </button>
                  ))}
                </div>

                {/* Upload */}
                {attachmentSource === 'upload' && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="rounded-lg border-2 border-dashed   px-6 py-8 w-full text-center hover:border-cyan-500/50 hover: transition-colors disabled:opacity-50"
                    >
                      {uploading ? (
                        <div className="space-y-2">
                          <p className=" text-sm font-medium" style={{ color: t.textSecondary }}>Uploading {uploadFileName}...</p>
                          <div className="w-48 mx-auto h-2 rounded-full overflow-hidden bg-slate-700">
                            <div className="h-full bg-cyan-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                          </div>
                          <p className="text-xs " style={{ color: t.textMuted }}>{uploadProgress}%</p>
                        </div>
                      ) : uploadFileName && fileUrl ? (
                        <div className="space-y-1">
                          <p className=" text-sm font-medium" style={{ color: t.accent }}>✓ {uploadFileName}</p>
                          <p className="text-xs " style={{ color: t.textMuted }}>Click to replace</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className=" text-sm font-medium" style={{ color: t.textSecondary }}>Click to select a file</p>
                          <p className="text-xs " style={{ color: t.textMuted }}>PDF, images, documents, etc.</p>
                        </div>
                      )}
                    </button>
                  </div>
                )}

                {/* Direct URL */}
                {attachmentSource === 'link' && (
                  <div>
                    <input
                      type="url"
                      placeholder="https://example.com/document.pdf"
                      value={fileUrl}
                      onChange={(e) => setFileUrl(e.target.value)}
                      className="w-full rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
                    />
                    <p className="mt-1 text-xs " style={{ color: t.textMuted }}>
                      Direct URL to a file. It will be embedded in the viewer.
                    </p>
                  </div>
                )}

                {/* Google Drive */}
                {attachmentSource === 'drive' && (
                  <div className="space-y-3">
                    {GOOGLE_CLIENT_ID && GOOGLE_APP_ID ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            if (pickerRef.current) pickerRef.current.visible = true;
                          }}
                          className="w-full rounded-lg border-2 border-dashed px-4 py-4 hover:border-cyan-500 transition-colors flex items-center justify-center gap-2" style={{ color: t.textSecondary }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 6 6.55-11.5zm1.14 0l6.57 11.5H22L15.43 3.5zm7.72 12.5H2.29l3.43 6h11.43z"/></svg>
                          Choose from Google Drive
                        </button>
                        <drive-picker
                          ref={pickerRef}
                          client-id={GOOGLE_CLIENT_ID}
                          app-id={GOOGLE_APP_ID}
                          title="Select a file from Google Drive"
                          nav-hidden="true"
                          style={{ display: 'none' }}
                        >
                          <drive-picker-docs-view></drive-picker-docs-view>
                        </drive-picker>
                      </>
                    ) : (
                      <p className="text-xs text-amber-400">
                        Google Drive Picker not configured. Paste a sharing link below instead.
                      </p>
                    )}

                    {/* Manual fallback — always available */}
                    <div className="flex gap-2">
                      <input
                        type="url"
                        placeholder="Or paste a Google Drive link..."
                        value={driveUrl}
                        onChange={(e) => setDriveUrl(e.target.value)}
                        className="flex-1 rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
                      />
                      <button
                        onClick={handleDriveApply}
                        disabled={!driveUrl.trim()}
                        className="rounded-lg bg-cyan-600 px-4 py-2 font-medium  hover:bg-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap" style={{ color: t.textPrimary }}
                      >
                        Apply
                      </button>
                    </div>
                    <p className="mt-1 text-xs " style={{ color: t.textMuted }}>
                      Use the picker above or paste a sharing link. File must be shared with "Anyone with the link".
                    </p>
                  </div>
                )}

                {/* Preview */}
                {fileUrl && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold  uppercase tracking-wide" style={{ color: t.textSecondary }}>Preview</span>
                      <button
                        onClick={async () => {
                          // Delete from Firebase Storage if it's a storage URL
                          if (storage && fileUrl.includes('firebasestorage.googleapis.com')) {
                            try {
                              const storageRef = ref(storage, fileUrl);
                              await deleteObject(storageRef);
                            } catch (err) {
                              console.warn('Could not delete storage file:', err);
                            }
                          }
                          setFileUrl(''); setDriveUrl(''); setUploadFileName('');
                        }}
                        className="text-xs  hover:text-red-300" style={{ color: t.fail }}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="rounded-lg border  overflow-hidden bg-white flex items-center justify-center" style={{ maxHeight: '300px' }}>
                      {/\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(fileUrl) || fileUrl.startsWith('blob:') ? (
                        <img src={fileUrl} alt="Attachment Preview" className="w-full h-auto object-contain" style={{ maxHeight: '300px' }} />
                      ) : (
                        <iframe src={fileUrl} className="w-full" style={{ height: '300px' }} title="Attachment Preview" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {itemType === 'weblink' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold  mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                    URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/resource"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="w-full rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold  mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                    Screenshot URL (optional)
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/screenshot.png"
                    value={screenshotUrl}
                    onChange={(e) => setScreenshotUrl(e.target.value)}
                    className="w-full rounded-lg border   px-4 py-2  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
                  />
                  <p className="mt-1 text-xs " style={{ color: t.textMuted }}>
                    Paste a direct image URL for a screenshot preview of the linked resource.
                  </p>
                  {screenshotUrl && (
                    <div className="mt-3 rounded-lg border  overflow-hidden">
                      <img src={screenshotUrl} alt="Screenshot preview" className="w-full max-h-64 object-contain " />
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && <AlertBanner variant="error" message={error} />}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  if (manualInfo) navigate(MANUAL_PAGE_PATH);
                  else if (existingItem) navigate(`/library/${existingItem.slug}`);
                  else navigate('/library');
                }}
                className="rounded-lg border   px-4 py-2 font-medium  hover:bg-slate-700 transition-colors" style={{ color: t.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-cyan-600 px-4 py-2 font-medium  hover:bg-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ color: t.textPrimary }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Linker Sidebar */}
        <div className="space-y-6">
          {/* Skill Trees */}
          <div className="rounded-lg border   p-6">
            <h3 className="text-lg font-semibold  mb-4" style={{ color: t.textPrimary }}>Skill Trees</h3>
            <div className="space-y-2">
              {SKILL_TREES.map((tree) => (
                <label
                  key={tree.id}
                  className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-colors" style={{ color: t.textSecondary }}
                >
                  <input
                    type="checkbox"
                    checked={skillTreeIds.has(tree.id)}
                    onChange={() => toggleSkillTree(tree.id)}
                    className="rounded  text-cyan-600 focus:ring-cyan-400"
                  />
                  <span>{tree.shortName}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Qualifications */}
          <div className="rounded-lg border   p-6">
            <h3 className="text-lg font-semibold  mb-4" style={{ color: t.textPrimary }}>Link to Quals</h3>
            <div className="space-y-2">
              {qualificationsData.map((qual) => (
                <label
                  key={qual.id}
                  className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-colors" style={{ color: t.textSecondary }}
                >
                  <input
                    type="checkbox"
                    checked={linkedQualIds.has(qual.id)}
                    onChange={() => toggleQualLink(qual.id)}
                    className="rounded  text-cyan-600 focus:ring-cyan-400"
                  />
                  <span>{qual.shortName}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
